import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, ChevronDownOutline, ChevronUpOutline, Flex, IconButton, StyleProps, useDisclosure } from '@bonusly/design-system';

import { Bonus } from '../../../../../types/bonus';
import { ReactComponent as Regenerate } from '../../assets/images/regenerate.svg';
import { SpacedSpinner } from '../../components/common/loading';
import { useBonusAnalytics } from '../../hooks/use-bonus-analytics';
import { useSuggestedResponses } from './hooks/use-suggested-responses';

interface SuggestedCommentsProps extends StyleProps {
	bonus: Bonus;
	onSetText: (s: string) => void;
	title: string;
	buttonLabel: string;
}

export const SuggestedComments: FC<SuggestedCommentsProps> = ({ bonus, onSetText, title, buttonLabel, ...rest }) => {
	const panel = useDisclosure({ defaultIsOpen: true });
	const { isFetched, isGenerating, isLoading, regenerate, suggestions } = useSuggestedResponses(bonus.id);

	const { t } = useTranslation('recognition');

	const { trackToggleSuggestedThankYous, trackClickSuggestedThankYou } = useBonusAnalytics();

	const toggleSuggestions = () => {
		trackToggleSuggestedThankYous({
			display: panel.isOpen ? 'hide' : 'show',
		});

		panel.onToggle();
	};

	const handleClick = (text: string) => {
		trackClickSuggestedThankYou();
		onSetText(text);
	};

	return (
		<Box bg="blue.25" borderRadius="sm" mb={2} p={3} pl={2} {...rest}>
			<Flex alignItems="center" justifyContent="space-between">
				<Box pl={1} fontSize="md">
					{title}
				</Box>

				<Box
					aria-label={t('bonus_input.quick_thank_you.toggle_panel') ?? ''}
					as="button"
					color="gray.500"
					cursor="pointer"
					fontSize="2xl"
					onClick={toggleSuggestions}
					px={2}
				>
					{panel.isOpen ? <ChevronUpOutline size="xs" /> : <ChevronDownOutline size="xs" />}
				</Box>
			</Flex>

			{panel.isOpen && (
				<Flex
					gap={2}
					pt={2}
					flexWrap={{ base: 'wrap', sm: 'nowrap' }}
					css={{
						scrollbarWidth: 'none',
						msOverflowStyle: 'none',
						'&::-webkit-scrollbar': {
							display: 'none',
						},
					}}
				>
					<Box flexBasis="100%" flexShrink={1}>
						<Flex flexWrap="wrap" gap={2} pl={1} pb={1}>
							{suggestions.map((text, i) => (
								<Button
									bg="white"
									colorScheme="secondary"
									key={i}
									minW="auto"
									onClick={() => handleClick(text)}
									shadow="sm"
									size="md"
									py={1}
									whiteSpace="normal"
									height="auto"
									workWrap="break-word"
									textAlign="left"
									variant="alpha"
								>
									{text}
								</Button>
							))}
						</Flex>

						{isLoading && <SpacedSpinner my={2} />}
					</Box>

					{isFetched && (
						<IconButton
							aria-label={buttonLabel}
							colorScheme="secondary"
							h={10}
							icon={<Regenerate />}
							isLoading={isGenerating}
							onClick={regenerate}
							variant="alpha"
						/>
					)}
				</Flex>
			)}
		</Box>
	);
};
